import { makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '~components/typography';
import { PicFilterNoResults } from '~svg-components';
import BookingCardRecentlyViewed from './booking-card-recently-viewed';
import NominationPlanCardRecentlyViewed from './nomination-plan-card-recently-viewed';
import StudentCardRecentlyViewed from './student-card-recently-viewed';

const useStyles = makeStyles(
  (theme) => ({
    root: {
      position: 'absolute',
      width: '616px',
      minHeight: '396px',
      padding: '16px 20px 24px',
      backgroundColor: theme.colors.white,
      zIndex: '999',
      boxShadow: theme.shadow.componentShallow,
      borderRadius: theme.borderRadius.big,
      border: `1px solid ${theme.colors.greyE8}`,
    },
    recentlyViewedRoot: {
      ...theme.flex.mainStart,
    },
    bookingContainer: {
      flex: 1,
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        width: 6,
        height: 6,
        display: 'block',
        backgroundColor: theme.colors.white,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.colors.greyE8,
        borderRadius: 20,
      },
    },
    studentContainer: {
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        width: 6,
        height: 6,
        display: 'block',
        backgroundColor: theme.colors.white,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.colors.greyE8,
        borderRadius: 20,
      },
    },
    notFoundResult: {
      ...theme.flex.fillCenter,
      height: '320px',
      width: '100%',
    },
    notFoundResultIcon: {
      width: '168px',
      height: '118px',
      marginBottom: '16px',
    },
  }),
  { name: 'SearchPanel' }
);

function RecentlyViewedPanel({ students, bookings, nominationPlans }) {
  const { t } = useTranslation('global-search');
  const classes = useStyles();

  const renderDivider = () => <div style={{ marginLeft: 16 }} />;

  const renderEmptyBookingContainer = () => (
    <div className={classes.notFoundResult}>
      <div style={{ textAlign: 'center' }}>
        <PicFilterNoResults className={classes.notFoundResultIcon} />
        <Typography variant="body1" themeColor="grey66">
          {t('no_booking_found')}
        </Typography>
      </div>
    </div>
  );

  return (
    <div className={classes.root}>
      <Typography variant="h6" themeColor="grey99" style={{ marginBottom: 12 }}>
        {t('recently_viewed')}
      </Typography>
      <div className={classes.recentlyViewedRoot}>
        {bookings?.length > 0 || nominationPlans?.length > 0 ? (
          <div
            className={classes.bookingContainer}
            style={{ maxHeight: window.innerHeight - 86 - 40 - 16 }}
          >
            {bookings.map((booking, index) => (
              <Fragment key={`booking-${index}`}>
                <BookingCardRecentlyViewed booking={booking} />
              </Fragment>
            ))}
            {nominationPlans.map((nominationPlan, index) => (
              <Fragment key={`nomination-plan-${index}`}>
                <NominationPlanCardRecentlyViewed nominationPlan={nominationPlan} />
              </Fragment>
            ))}
          </div>
        ) : (
          renderEmptyBookingContainer()
        )}

        {(bookings?.length > 0 || nominationPlans?.length > 0) &&
          students?.length > 0 &&
          renderDivider()}

        {students?.length > 0 && (
          <div
            className={classes.studentContainer}
            style={{ maxHeight: window.innerHeight - 86 - 40 - 16 }}
          >
            {students.map((student, index) => (
              <Fragment key={`student-${index}`}>
                <StudentCardRecentlyViewed student={student} />
              </Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

RecentlyViewedPanel.prototype = {
  students: PropTypes.array,
  bookings: PropTypes.array,
};

RecentlyViewedPanel.defaultProps = {
  students: [],
  bookings: [],
};

export default RecentlyViewedPanel;
